import { useEffect, useState } from "react";
import type { Book as EpubBook } from "epubjs";
import { computeBookStats, type BookStats, type WordCount } from "@/services/bookInsightsService";
import { listHighlights } from "@/services/notesService";

interface Props {
  book: EpubBook | null;
  bookId: string;
  bookTitle: string;
  bookAuthor?: string;
  language?: string;
  onClose: () => void;
}

const ACCENT = "#f7931a";
const LANG: Record<string, string> = {
  en: "English", de: "German", fr: "French", es: "Spanish", it: "Italian",
  ja: "Japanese", ru: "Russian", "de-de": "German", "en-us": "English",
};

export function BookInsights({
  book,
  bookId,
  bookTitle,
  bookAuthor,
  language,
  onClose,
}: Props) {
  const [stats, setStats] = useState<BookStats | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        if (!book) throw new Error("book not loaded");
        const highlights = await listHighlights(bookId);
        const s = await computeBookStats(book, highlights, language);
        if (alive) setStats(s);
      } catch (e) {
        if (alive) setError((e as Error).message || "couldn't compute insights");
      }
    })();
    return () => { alive = false; };
  }, [book, bookId, language]);

  const langName = language ? (LANG[language.toLowerCase()] ?? language) : undefined;
  const maxChapter = stats ? Math.max(1, ...stats.chapters.map((c) => c.words)) : 1;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden />
      <div className="pv-surface pv-border pv-fg fixed inset-x-0 top-1/2 z-50 mx-auto flex max-h-[85vh] w-[90vw] max-w-lg -translate-y-1/2 flex-col overflow-y-auto rounded-lg border p-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-semibold">Book insights</div>
            <div className="pv-fg-soft text-xs">{bookTitle}{bookAuthor ? ` · ${bookAuthor}` : ""}</div>
          </div>
          <button onClick={onClose} className="pv-fg-soft text-sm hover:underline">Close</button>
        </div>

        {!stats && !error && (
          <div className="pv-fg-soft py-10 text-center text-sm">Reading the whole book…</div>
        )}
        {error && <div className="mt-4 text-sm text-red-400">{error}</div>}

        {stats && (
          <div className="mt-4 space-y-5">
            {/* Stat tiles */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Words" value={stats.totalWords.toLocaleString()} />
              <Stat label="Chapters" value={String(stats.chapterCount)} />
              <Stat label="Reading time" value={fmtTime(stats.readingMinutes)} />
              <Stat label="Language" value={langName || "—"} />
            </div>

            {/* Words per chapter */}
            {stats.chapters.length > 1 && (
              <div>
                <SectionTitle>Words per chapter</SectionTitle>
                <div className="mt-2 max-h-40 space-y-1 overflow-y-auto pr-1">
                  {stats.chapters.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="pv-fg-soft w-28 shrink-0 truncate text-[11px]" title={c.title}>
                        {c.title}
                      </span>
                      <div className="pv-surface-2 h-2 flex-1 overflow-hidden rounded-full">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(c.words / maxChapter) * 100}%`, background: ACCENT }}
                        />
                      </div>
                      <span className="pv-fg-soft w-12 shrink-0 text-right text-[11px] tabular-nums">
                        {c.words.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Book word cloud */}
            {stats.bookCloud.length > 0 && (
              <div>
                <SectionTitle>Word cloud — the book</SectionTitle>
                <Cloud words={stats.bookCloud} />
              </div>
            )}

            {/* Highlights word cloud */}
            {stats.highlightCloud.length > 0 && (
              <div>
                <SectionTitle>Word cloud — your highlights</SectionTitle>
                <Cloud words={stats.highlightCloud} accent />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="pv-surface-2 pv-border rounded-lg border px-3 py-2">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="pv-fg-soft text-[11px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="pv-fg-soft text-[11px] font-semibold uppercase tracking-wider">{children}</div>
  );
}

function Cloud({ words, accent }: { words: WordCount[]; accent?: boolean }) {
  const max = Math.max(1, ...words.map((w) => w.count));
  const min = Math.min(...words.map((w) => w.count));
  const size = (c: number) => 12 + Math.round(((c - min) / Math.max(1, max - min)) * 20); // 12–32px
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1">
      {words.map((w) => (
        <span
          key={w.word}
          title={`${w.count}×`}
          style={{
            fontSize: size(w.count),
            lineHeight: 1.1,
            color: accent
              ? ACCENT
              : `rgba(232,230,225,${0.5 + 0.5 * ((w.count - min) / Math.max(1, max - min))})`,
            fontWeight: w.count > (max + min) / 2 ? 600 : 400,
          }}
        >
          {w.word}
        </span>
      ))}
    </div>
  );
}

function fmtTime(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
